import { getSupabaseClient } from "./supabaseClient";
import { AuthService } from "./AuthService";
import { OnlineFeaturesStorage } from "./OnlineFeaturesStorage";
import { RunQueue, type FlushOutcome } from "./RunQueue";
import { callFunction } from "./functionsClient";
import { validateDisplayName } from "./validation";
import type { Platform } from "@/platform/Platform";
import type { LeaderboardService, SubmitRunOutcome } from "./LeaderboardService";
import type {
  LevelLeaderboardEntry,
  MyLevelRank,
  MyWorldRank,
  RunSubmission,
  ServiceResult,
  WorldLeaderboardEntry,
} from "./types";

interface LevelLeaderboardRow {
  rank: number;
  display_name: string;
  score: number;
  stars: 0 | 1 | 2 | 3;
}

interface MyLevelRankRow {
  rank: number | null;
  score: number;
  stars: 0 | 1 | 2 | 3;
  has_score: boolean;
}

interface WorldLeaderboardRow {
  rank: number;
  display_name: string;
  total_score: number;
  total_stars: number;
}

interface MyWorldRankRow {
  rank: number | null;
  total_score: number;
  total_stars: number;
  has_score: boolean;
}

/**
 * The real online implementation: reads go through the public
 * SECURITY DEFINER RPC functions using the anon/authenticated Supabase
 * session directly; writes (name, run submission) go through the
 * authenticated Netlify Functions, never a direct table write - the
 * browser never has write access to profiles/level_scores/game_runs (RLS
 * blocks it even if it tried).
 *
 * Every score-based leaderboard surface (per-level Top 10, world/global
 * aggregate, and "my rank" for both) reads the `_v2` RPC variant added by
 * `supabase/migrations/003_scoring_v2.sql` - Scoring V2 is the default,
 * only leaderboard after launch. The plain (non-`_v2`) RPCs still exist
 * server-side, filtered to `scoring_version = 1`, purely as a legacy-data
 * read path - this client never calls them.
 *
 * Every public method resolves, never throws: any network/Supabase
 * failure is caught and turned into `{ state: "offline" }` (when the
 * browser is known to be offline) or `{ state: "error", message }`
 * (a generic, non-leaking message - never a raw Postgres/Supabase error).
 */
class SupabaseLeaderboardServiceImpl implements LeaderboardService {
  isAvailable(): boolean {
    return OnlineFeaturesStorage.isEnabled() && getSupabaseClient() !== null;
  }

  /**
   * Scoring V2: the global leaderboard defaults to V2-only after launch -
   * this reads `get_level_leaderboard_v2`/`get_my_level_rank_v2`
   * (Final Score, never mixed with V1 Bite-Score-only rows), added by
   * `supabase/migrations/003_scoring_v2.sql`. The row shape (rank/
   * displayName/score/stars) is unchanged - only what `score` and the
   * ranking order mean underneath changed, so `LeaderboardScene` needed
   * no changes at all.
   */
  async getLevelLeaderboard(levelId: string, platform: Platform): Promise<ServiceResult<LevelLeaderboardEntry[]>> {
    if (!this.isAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    try {
      const { data, error } = await client.rpc("get_level_leaderboard_v2", { p_level_id: levelId, p_platform: platform });
      if (error) return this.classifyError();
      const rows = (data ?? []) as LevelLeaderboardRow[];
      return {
        state: "ok",
        data: rows.map((row) => ({ rank: row.rank, displayName: row.display_name, score: row.score, stars: row.stars })),
      };
    } catch {
      return this.classifyError();
    }
  }

  async getMyLevelRank(levelId: string, platform: Platform): Promise<ServiceResult<MyLevelRank>> {
    if (!this.isAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    if (!(await AuthService.ensureSignedIn())) return this.classifyError();
    try {
      const { data, error } = await client.rpc("get_my_level_rank_v2", { p_level_id: levelId, p_platform: platform });
      if (error) return this.classifyError();
      const row = ((data ?? []) as MyLevelRankRow[])[0];
      if (!row) return { state: "ok", data: { hasScore: false, rank: null, score: 0, stars: 0 } };
      return { state: "ok", data: { hasScore: row.has_score, rank: row.rank, score: row.score, stars: row.stars } };
    } catch {
      return this.classifyError();
    }
  }

  /**
   * Scoring V2: same rule as `getLevelLeaderboard()` above - the world/
   * global leaderboard also defaults to V2-only after launch, reading
   * `get_world_leaderboard_v2` (a sum of V2-only best scores, requiring a
   * V2 result on every level in the world to qualify - see the migration).
   */
  async getWorldLeaderboard(worldId: string, platform: Platform): Promise<ServiceResult<WorldLeaderboardEntry[]>> {
    if (!this.isAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    try {
      const { data, error } = await client.rpc("get_world_leaderboard_v2", { p_world_id: worldId, p_platform: platform });
      if (error) return this.classifyError();
      const rows = (data ?? []) as WorldLeaderboardRow[];
      return {
        state: "ok",
        data: rows.map((row) => ({
          rank: row.rank,
          displayName: row.display_name,
          totalScore: row.total_score,
          totalStars: row.total_stars,
        })),
      };
    } catch {
      return this.classifyError();
    }
  }

  async getMyWorldRank(worldId: string, platform: Platform): Promise<ServiceResult<MyWorldRank>> {
    if (!this.isAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    if (!(await AuthService.ensureSignedIn())) return this.classifyError();
    try {
      const { data, error } = await client.rpc("get_my_world_rank_v2", { p_world_id: worldId, p_platform: platform });
      if (error) return this.classifyError();
      const row = ((data ?? []) as MyWorldRankRow[])[0];
      if (!row) return { state: "ok", data: { hasScore: false, rank: null, totalScore: 0, totalStars: 0 } };
      return {
        state: "ok",
        data: { hasScore: row.has_score, rank: row.rank, totalScore: row.total_score, totalStars: row.total_stars },
      };
    } catch {
      return this.classifyError();
    }
  }

  async getMyDisplayName(): Promise<ServiceResult<string | null>> {
    if (!this.isAvailable()) return { state: "offline" };
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    if (!(await AuthService.ensureSignedIn())) return this.classifyError();
    const playerId = AuthService.getPlayerId();
    if (!playerId) return this.classifyError();
    try {
      const { data, error } = await client.from("profiles").select("display_name").eq("id", playerId).maybeSingle();
      if (error) return this.classifyError();
      return { state: "ok", data: data?.display_name ?? null };
    } catch {
      return this.classifyError();
    }
  }

  async setDisplayName(name: string): Promise<ServiceResult<string>> {
    if (!this.isAvailable()) return { state: "offline" };
    const validation = validateDisplayName(name);
    if (!validation.valid) return { state: "error", message: validation.error ?? "Invalid name." };
    if (!(await AuthService.ensureSignedIn())) return this.classifyError();
    const token = await this.getAccessToken();
    if (!token) return this.classifyError();

    const result = await callFunction<{ displayName: string }>("profile-name", token, { displayName: validation.normalized });
    if (!result.ok) return result.status >= 400 && result.status < 500 ? { state: "error", message: result.message } : this.classifyError();
    return { state: "ok", data: result.data.displayName };
  }

  /**
   * `RunQueue.enqueue(run)` happens synchronously, before any `await` at
   * all - the run is durably on disk (localStorage) before this function
   * ever yields to the event loop. That matters because the caller (e.g.
   * `GameScene.handleLevelComplete()`) doesn't - and shouldn't have to -
   * await this call before letting a Scene transition or teardown happen;
   * a completed run must never depend on the Scene/network racing to
   * finish in time to be recorded at all. Everything after the enqueue is
   * just "try to flush right now" - `flushQueueInternal()` (shared with
   * `flushQueue()`) removes it from the queue the moment it's actually
   * confirmed sent (or permanently rejected); until then it simply stays
   * queued for the next retry, same as any other offline run.
   */
  async submitRun(run: RunSubmission): Promise<SubmitRunOutcome> {
    if (!this.isAvailable()) return "disabled";
    RunQueue.enqueue(run);
    const outcomes = await this.flushQueueInternal();
    return outcomes.get(run.runId) === "sent" ? "sent" : "queued";
  }

  async flushQueue(): Promise<void> {
    await this.flushQueueInternal();
  }

  private async flushQueueInternal(): Promise<Map<string, FlushOutcome>> {
    if (!this.isAvailable()) return new Map();
    if (!(await AuthService.ensureSignedIn())) return new Map();

    return RunQueue.flush(async (run) => {
      const token = await this.getAccessToken();
      if (!token) return "retry-later";
      const result = await callFunction("submit-run", token, run);
      if (result.ok) return "sent";
      // A 4xx means the server rejected this exact payload as invalid -
      // retrying an unchanged payload would only fail the same way again.
      if (result.status >= 400 && result.status < 500) return "drop";
      return "retry-later";
    });
  }

  /**
   * Same defensive wrap as `ScoreChallengeClient.ensureSignedIn()`/
   * `DailyChallengeClient.ensureSignedIn()`: an unexpected rejection from
   * `client.auth.getSession()` must resolve to "no token" (routing the
   * caller into its existing `"retry-later"`/offline handling) rather than
   * becoming an unhandled rejection inside `RunQueue.flush()`'s submit
   * callback, which has no try/catch of its own around this call.
   */
  private async getAccessToken(): Promise<string | null> {
    try {
      const client = getSupabaseClient();
      if (!client) return null;
      const { data } = await client.auth.getSession();
      return data.session?.access_token ?? null;
    } catch {
      return null;
    }
  }

  private classifyError(): ServiceResult<never> {
    const knownOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    return knownOffline ? { state: "offline" } : { state: "error", message: "Something went wrong. Please try again." };
  }
}

export const supabaseLeaderboardService = new SupabaseLeaderboardServiceImpl();
